import { cleaningActionErrorMessage } from '@/components/cleaning/action-error-copy'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { Link, useSearchParams } from 'react-router'
import {
  CalendarDays,
  Check,
  GripVertical,
  MessageSquareText,
  RefreshCw,
  StickyNote,
  TriangleAlert,
} from 'lucide-react'
import { api } from '@/api/client'
import type {
  CleaningActionItem,
  CleaningActionKind,
  CleaningItemStatus,
  CleaningJob,
  CleaningTeam,
} from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { ErrorState } from '@/components/ui/error-state'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { TentativeBadge } from '@/components/cleaning/tentative-badge'
import { useToast } from '@/hooks/use-toast'
import { UndoAction } from '@/components/cleaning/undo-action'
import { useUndoCleaningAction } from '@/components/cleaning/use-undo-cleaning-action'
import {
  withSettledRows,
  type DisplayRow,
  type SettledRow,
} from '@/components/cleaning/settled-rows'
import { useProperties } from '@/hooks/useApi'
import { pickableProperties } from '@/lib/property-lifecycle'
import { CleaningFilterBar, type CleaningFacets } from '@/components/cleaning/filter-bar'
import { CleaningDecideDialog } from '@/components/cleaning/decide-dialog'
import { CleaningJobDrawer } from '@/components/cleaning/job-drawer'
import { applyCleaningJob } from '@/components/cleaning/cache'
import { DayDragProvider, DayGroup, DraggableRow } from '@/components/cleaning/day-drag'
import { groupByDay } from '@/components/cleaning/day-move'
import { JobCalendarDialog } from '@/components/cleaning/job-calendar-dialog'
import { CrewMessageDialog } from '@/components/cleaning/crew-message-dialog'
import { AskedBadge, WaitingAsks } from '@/components/cleaning/crew-asks'
import { useAskedAt, useWaitingAsks } from '@/components/cleaning/crew-asks-query'
import { CleaningReservationDialog } from '@/components/cleaning/reservation-dialog'
import { InfiniteListFooter, LoadEarlierButton } from '@/components/cleaning/infinite-list'
import { MutedCrewsNotice } from '@/components/cleaning/muted-crews-notice'
import {
  readCleaningFilters,
  writeCleaningFilters,
  type CleaningFilters,
} from '@/components/cleaning/filters'
import {
  blockLabel,
  CLEANING_ITEM_STATUS_META,
  CLEANING_KIND_META,
  cleaningStateBadge,
  DEFAULT_PROPERTY_TIME_ZONE,
  formatCleaningDay,
  formatMovedFrom,
  formatStay,
  localDate,
  reasonTags,
  shiftCalendarDate,
  shortTime,
} from '@/components/cleaning/presentation'

/**
 * The cleaning tracker's operator screen (CAP-374, reworked for scale by
 * CAP-412).
 *
 * Two tabs, because there are exactly two questions this screen answers: what
 * do I have to do right now, and what is coming.
 *
 * **Nothing here is computed from business rules.** Which rows exist, their
 * order, each job's substate, the property's own calendar day and whether a job
 * may be completed all arrive already decided from admin-api, from the same
 * functions the daily digest uses, so the screen and the email can never
 * disagree. What is derived here is presentational only.
 *
 * **Why a table rather than a card per row.** Production returns 580 action
 * rows. A card spends roughly 120px of height saying what a 45px row says, and
 * 580 of them is not a list anyone can work from. The rows are grouped by kind,
 * which costs nothing because admin-api already returns them in kind order -
 * that order IS its answer to "what do I do next", so this screen groups rather
 * than re-ranks.
 */

/** How many rows a batch loads. */
const BATCH = 50

/** How many days one "load previous" press reaches back. */
const PAST_WEEK_DAYS = 7

/**
 * admin-api's page cap, so one request carries the whole week. The portfolio
 * cleans at most a few times a property a week, an order of magnitude below
 * this - a week that ever outgrew it would lose its oldest rows, not error.
 */
const PAST_WEEK_LIMIT = 500

/** Stable identity, so gating the past off does not invalidate the merge memo. */
const EMPTY_JOBS: CleaningJob[] = []

/** Keeps the cache keyed by exactly the filters that change the request. */
function actionsQueryKey(filters: CleaningFilters) {
  return [
    'cleaning',
    'actions-needed',
    filters.property,
    filters.crew,
    filters.kinds.join(','),
  ] as const
}

/**
 * One key per direction (CAP-526).
 *
 * Both stay under the `['cleaning', 'jobs']` prefix, which is not incidental:
 * `applyCleaningJob` patches every cache entry under it after an action, and a
 * direction that sat outside would quietly stop reflecting the job the
 * operator just changed.
 *
 * The split between the two halves is sent as a relative bound (`today-1`)
 * that admin-api resolves against each property's own day, so no date is part
 * of the key: a refetch after midnight asks the same question and gets the new
 * day's answer (CAP-724).
 */
function jobsQueryKey(filters: CleaningFilters, direction: 'past' | 'past-count' | 'future') {
  return [
    'cleaning',
    'jobs',
    direction,
    filters.property,
    filters.crew,
    filters.substates.join(','),
  ] as const
}

type ActionsPage = Awaited<ReturnType<typeof api.getCleaningActionItems>>
type JobsPage = Awaited<ReturnType<typeof api.getCleaningJobs>>

/**
 * One week of the past. Still a `JobsPage` underneath, which is what lets
 * `applyCleaningJob` patch it like every other entry under the jobs prefix.
 */
type PastWeekPage = JobsPage & {
  /** Where the next week ends: the newest cleaning before this one. */
  next_end: string | null
  /** Past cleanings still older than this week. */
  earlier: number
}

/**
 * Something the screen knows but an empty list cannot say.
 *
 * Rendered OUTSIDE the empty/non-empty branch on purpose. A clipped scan that
 * returned no rows is the case where this matters most, and hiding the warning
 * exactly then turns "we could not read some properties" into "there is nothing
 * to do" - the one reading that gets acted on.
 */
function Notice({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-card border border-amber/30 bg-warningBg p-3 text-sm text-text">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber" />
      <span>{children}</span>
    </div>
  )
}

/** A dot in the crew's own colour, so a day's mix is readable at a glance. */
function CrewDot({ color }: { color: string | null }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-2 w-2 shrink-0 rounded-full"
      style={{ backgroundColor: color ?? 'var(--color-textFaint)' }}
    />
  )
}

function teamLabel(teamId: string | null | undefined, teams: CleaningTeam[]): string {
  if (!teamId) return 'No crew'
  return teams.find(team => team.id === teamId)?.short_name ?? teamId
}

function teamColor(teamId: string | null | undefined, teams: CleaningTeam[]): string | null {
  if (!teamId) return null
  return teams.find(team => team.id === teamId)?.color ?? null
}

function CrewCell({ teamId, teams }: { teamId: string | null | undefined; teams: CleaningTeam[] }) {
  if (!teamId) return <span className="italic text-textFaint">No crew</span>
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-textMuted">
      <CrewDot color={teamColor(teamId, teams)} />
      {teamLabel(teamId, teams)}
    </span>
  )
}

/**
 * What a toast calls an action once it has landed.
 *
 * Kept apart from the BUTTON's label rather than derived from it: the button
 * is an instruction and the toast is a report, and the previous
 * `${meta.action} recorded` turned "Record ack" into "Record ack recorded"
 * and "Told them" into "Told them recorded".
 */
const ACTION_DONE: Record<'ack' | 'told-them' | 'unpin', string> = {
  ack: 'Ack recorded',
  'told-them': 'Marked as told',
  unpin: 'Unpinned',
}

/** Severity as form, not only as a word, readable down the left edge. */
const TONE_STRIPE: Record<string, string> = {
  destructive: 'var(--color-danger)',
  warning: 'var(--color-amber)',
  success: 'var(--color-mint)',
  secondary: 'var(--color-textFaint)',
}

/**
 * DOM id for one action row, matching the `propertyId:jobId` pair the cleaning
 * heads-up emails put in their `?job=` link. Encoded because a job id contains
 * a colon of its own (`reservation:HMABC`), and an un-encoded pair would be
 * ambiguous about where the property id ends.
 *
 * Named for a card because that is what these rows were when the emails
 * started linking to them (CAP-407). The links are already out in people's
 * inboxes, so the id they point at keeps its shape.
 */
export function actionCardDomId(propertyId: string, jobId: string): string {
  return `cleaning-job-${encodeURIComponent(propertyId)}-${encodeURIComponent(jobId)}`
}

/**
 * A job's identity on this board.
 *
 * A cleaning job id is unique only WITHIN a property: an external block's id is
 * its start date, so two properties blocked from the same day carry the
 * identical `external_block:2026-09-25` (CAP-416). Every key, set and DOM id
 * here is therefore built from the PAIR - keying on `job_id` alone made React
 * render duplicate rows, disabled a second property's buttons, and retired its
 * row when the first was acted on, losing an undecided block silently.
 */
export function jobKey(propertyId: string, jobId: string): string {
  return `${propertyId}:${jobId}`
}

/** DOM id for a row's crew picker, so its label points at its own select. */
function assignSelectDomId(propertyId: string, jobId: string): string {
  return `assign-${actionCardDomId(propertyId, jobId)}`
}

/**
 * The `propertyId:jobId` pair from a `?job=` link, or null.
 *
 * Split on the FIRST colon only: a property id never contains one and a job id
 * routinely does, so splitting on all of them would truncate every reservation
 * job to `reservation`.
 */
export function parseJobParam(value: string | null): { propertyId: string; jobId: string } | null {
  if (!value) return null
  const separator = value.indexOf(':')
  if (separator <= 0 || separator === value.length - 1) return null
  return { propertyId: value.slice(0, separator), jobId: value.slice(separator + 1) }
}

/** The job a drawer is open on, or null. */
interface OpenJob {
  propertyId: string
  jobId: string
  propertyName: string
}

/** The stay a reservation dialog is open on, and the row that named it. */
interface OpenStay {
  reservationId: string
  propertyId: string
  propertyName: string
  /** The stay's check-in day in the property's zone, which the month opens on. */
  stayDay: string | null
}

/** The job a context calendar is open on, plus the day it centres on. */
interface CalendarJob extends OpenJob {
  day: string | null
}

/**
 * The per-row handle onto the property's month around this job (CAP-514).
 *
 * An icon rather than a word, in a gutter of its own rather than inside the
 * property cell: a column this narrow costs nothing on a 900px table, and the
 * alternative - a second control wedged between the property name and its
 * badges - makes every row's most-read cell busier for the sake of a thing
 * that is only wanted on some rows.
 */
function CalendarButton({ job, onOpen }: { job: CalendarJob; onOpen: (job: CalendarJob) => void }) {
  return (
    <button
      type="button"
      // The row itself opens the drawer, so this must not also.
      onClick={event => {
        event.stopPropagation()
        onOpen(job)
      }}
      title="See the month around this cleaning"
      aria-label={`See the month around the cleaning at ${job.propertyName}`}
      className="inline-flex h-6 w-6 items-center justify-center rounded text-textFaint hover:bg-surface2 hover:text-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint"
    >
      <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  )
}

interface ActionCallArgs {
  propertyId: string
  jobId: string
  action: Parameters<typeof api.runCleaningJobAction>[2]
  body?: Record<string, unknown>
  /** What the toast says once it lands: "Ack recorded". */
  describe: string
  /**
   * Which job it landed on - property, day and crew - for the toast's second
   * line.
   *
   * A confirmation that names only the action ("Told them recorded") is no
   * help to someone who has just realised they clicked the wrong row: it
   * cannot tell them whether the row was the wrong one. Composed at the call
   * site because the acting row is the only place that knows, and because an
   * assignment's crew is the one being chosen rather than the one on the job.
   */
  context: string
  /**
   * Which of the job's reasons offered it, so they can be retired on success.
   * More than one when two reasons offer the same control (CAP-530).
   */
  reasons: readonly CleaningActionKind[]
}

export function CleaningPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => readCleaningFilters(searchParams), [searchParams])
  // Memoized on the raw parameter, not recomputed per render: an effect that
  // depends on this object would otherwise see a new identity every render and
  // re-fire forever - which, for the paging effect below, means asking for the
  // next page in a loop.
  const jobParam = searchParams.get('job')
  const linkedJob = useMemo(() => parseJobParam(jobParam), [jobParam])
  const linkedJobKey = linkedJob ? jobKey(linkedJob.propertyId, linkedJob.jobId) : null
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [pendingJobs, setPendingJobs] = useState<ReadonlySet<string>>(() => new Set())
  // Rows settled from this page, kept on screen greyed until it reloads
  // (CAP-785). Component state on purpose: a reload is how they are cleared.
  const [settled, setSettled] = useState<ReadonlyArray<SettledRow<CleaningActionItem>>>([])
  const [openJob, setOpenJob] = useState<OpenJob | null>(null)
  const [calendarJob, setCalendarJob] = useState<CalendarJob | null>(null)
  const [openStay, setOpenStay] = useState<OpenStay | null>(null)
  const [crewMessageOpen, setCrewMessageOpen] = useState(false)

  const setFilters = useCallback(
    (next: CleaningFilters) => {
      // `replace`, so narrowing a list does not bury the previous page behind a
      // dozen history entries - Back should leave the board, not step through
      // every filter you tried.
      setSearchParams(writeCleaningFilters(next), { replace: true })
    },
    [setSearchParams]
  )

  const teamsQuery = useQuery({
    queryKey: ['cleaning', 'teams'],
    queryFn: () => api.getCleaningTeams(),
  })
  const settingsQuery = useQuery({
    queryKey: ['cleaning', 'settings'],
    queryFn: () => api.getCleaningSettings(),
  })
  // The shared hook, not a second inline query: it keys on
  // ['properties', 'active'] with a staleTime, so this page reuses whatever the
  // properties screen already fetched instead of caching a duplicate.
  const propertiesQuery = useProperties()

  // Read here as well as by the section that lists them, so the empty state
  // can wait for it (React Query serves both from one request).
  const waitingAsks = useWaitingAsks()
  /**
   * Whether an empty Needs action may NOT say "Nothing needs doing" (CAP-710):
   * while the crew replies still waiting are loading (a placeholder shows),
   * when they failed to load (unknown is not clear; the section above offers
   * Retry), and when there are some - a reply still to record is something to
   * do even with every job acked some other way.
   */
  const cleanSlateWithheld =
    waitingAsks.isLoading ||
    waitingAsks.isError ||
    (waitingAsks.data?.asks.length ?? 0) > 0 ||
    Boolean(waitingAsks.data?.truncated)

  const actionsQuery = useInfiniteQuery({
    queryKey: actionsQueryKey(filters),
    queryFn: ({ pageParam }) =>
      api.getCleaningActionItems({
        propertyId: filters.property || undefined,
        teamId: filters.crew || undefined,
        kinds: filters.kinds.length > 0 ? filters.kinds : undefined,
        limit: BATCH,
        offset: pageParam,
      }),
    initialPageParam: 0,
    // The next offset is what has been RECEIVED so far, not page index times
    // batch size: a short page (the server capping `limit`, or jobs retired
    // between requests) would otherwise skip whatever the difference was.
    getNextPageParam: (lastPage: ActionsPage, pages: ActionsPage[]) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0)
      return loaded < lastPage.total ? loaded : undefined
    },
    // Deliberately NOT gated on the tab. The "Needs action" badge reads this
    // query's `total_unfiltered`, and gating it meant a cold link straight to
    // ?tab=upcoming showed no badge at all - hiding the count of outstanding
    // work exactly when the operator is looking somewhere else. That count is
    // the one number on this screen that must never quietly disappear.
  })

  const commonJobFilters = {
    propertyId: filters.property || undefined,
    teamId: filters.crew || undefined,
    substates: filters.substates.length > 0 ? filters.substates : undefined,
    limit: BATCH,
  }

  // Where the list opens: YESTERDAY, not today. Yesterday's cleanings are the
  // ones still being checked on (did the crew get there, is it done), so they
  // belong on screen without a press of "Load previous".
  //
  // Sent as relative bounds admin-api resolves against each property's own
  // day, never a date computed here: the browser's timezone is not the
  // property's. They used to be computed from the actions list's `today`,
  // which made this tab wait for the slowest endpoint on the screen before it
  // could even start (CAP-724).
  const START = 'today-1'
  const BEFORE_START = 'today-2'

  // Yesterday onwards, oldest first. What the tab opens on, and the only
  // direction that loads without being asked.
  const futureQuery = useInfiniteQuery({
    queryKey: jobsQueryKey(filters, 'future'),
    queryFn: ({ pageParam }) =>
      api.getCleaningJobs({ ...commonJobFilters, from: START, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: JobsPage, pages: JobsPage[]) => {
      const loaded = pages.reduce((sum, page) => sum + page.jobs.length, 0)
      return loaded < lastPage.total ? loaded : undefined
    },
    enabled: filters.tab === 'all',
  })

  // How much past there is, and nothing else. One row, fetched with the tab,
  // because its `total` is the difference between an honest row count and one
  // that silently ignores four months of finished work.
  const pastCountQuery = useQuery({
    queryKey: jobsQueryKey(filters, 'past-count'),
    queryFn: () =>
      api.getCleaningJobs({
        ...commonJobFilters,
        // The last day of the past: the day before the list opens on.
        to: BEFORE_START,
        order: 'desc',
        limit: 1,
      }),
    enabled: filters.tab === 'all',
  })

  /**
   * Whether the reader has asked for the past at all.
   *
   * Nothing before yesterday loads until they press the button, which is what
   * lets the tab open on yesterday: with nothing above the boundary, yesterday
   * IS the top of the list.
   *
   * Which view the answer belongs to is stored WITH it and compared during
   * render, rather than reset from an effect. A filter change makes the loaded
   * past a different set, so "wanted" must not survive it - but resetting in an
   * effect means one render where the old answer is still live.
   */
  const viewKey = [filters.property, filters.crew, filters.substates.join(',')].join('|')
  const [pastState, setPastState] = useState({ view: viewKey, wanted: false })
  const pastWanted = pastState.view === viewKey && pastState.wanted
  const revealPast = useCallback(() => setPastState({ view: viewKey, wanted: true }), [viewKey])

  /**
   * The past, a week per press, newest week first.
   *
   * **On demand, never on scroll.** Loading upward when the reader reached the
   * top of the list made the top unreachable: every arrival at it prepended
   * another batch, so the tabs and the page header above the list could not be
   * got back to. A button loads only when pressed, and the rows it loads land
   * directly below it, in view.
   *
   * **A week, not a row count**, because a week is the unit an operator asks
   * about ("what happened last week"), and a batch of 50 rows cuts a day in
   * half. Each week ends at the newest cleaning still unloaded rather than on
   * the day before the last one loaded, so a quiet month costs one press rather
   * than four presses that each load nothing.
   */
  // The newest past cleaning is part of the key. A refetch replays the stored
  // week ends, so if a newer past cleaning appears (a job derived for
  // an earlier day, then Refresh) the first week would never reach it - counted in
  // the total, missing from the list. A new newest date starts the past over.
  const newestPast = pastCountQuery.data?.jobs[0]?.local_date ?? ''
  const pastQuery = useInfiniteQuery({
    queryKey: [...jobsQueryKey(filters, 'past'), newestPast],
    // Two requests per press, in PARALLEL: the week itself, and one row from
    // before it, which says where the next week ends and how many are left.
    // Each costs a portfolio-wide read in admin-api whatever its limit, so
    // running them back to back doubled the wait for no reason.
    queryFn: async ({ pageParam: end }): Promise<PastWeekPage> => {
      const from = shiftCalendarDate(end, -(PAST_WEEK_DAYS - 1))
      const [week, before] = await Promise.all([
        api.getCleaningJobs({
          ...commonJobFilters,
          from,
          to: end,
          order: 'desc',
          limit: PAST_WEEK_LIMIT,
        }),
        api.getCleaningJobs({
          ...commonJobFilters,
          to: shiftCalendarDate(from, -1),
          order: 'desc',
          limit: 1,
        }),
      ])
      return {
        ...week,
        // Bounded queries exclude rows without a `local_date`, so a returned
        // row always has one; the fallback is for the type.
        next_end: before.jobs[0]?.local_date ?? null,
        earlier: before.total,
        truncated: week.truncated || before.truncated,
      }
    },
    // The first week ends at the newest past cleaning, which the count query
    // has already fetched - so the first press is one round trip, not two.
    initialPageParam: newestPast,
    getNextPageParam: (lastPage: PastWeekPage) => lastPage.next_end ?? undefined,
    enabled: filters.tab === 'all' && pastWanted && Boolean(newestPast),
  })

  const teams = teamsQuery.data?.teams ?? []
  const actionPages = useMemo(() => actionsQuery.data?.pages ?? [], [actionsQuery.data])
  const futurePages = useMemo(() => futureQuery.data?.pages ?? [], [futureQuery.data])
  const pastPages = useMemo(() => pastQuery.data?.pages ?? [], [pastQuery.data])
  const first = actionPages[0]
  const firstJobs = futurePages[0]
  const pastCount = pastCountQuery.data
  const pastTruncated = pastCount?.truncated || pastPages.some(page => page.truncated)

  const items = useMemo(() => actionPages.flatMap(page => page.items), [actionPages])
  const actionsView = JSON.stringify(actionsQueryKey(filters))
  const rows = useMemo(
    () =>
      withSettledRows(
        items,
        settled.filter(row => row.view === actionsView),
        item => jobKey(item.property_id, item.job_id)
      ),
    [items, settled, actionsView]
  )
  const futureJobs = useMemo(() => futurePages.flatMap(page => page.jobs), [futurePages])
  // Reversed once here rather than sorted again: admin-api returned these
  // newest-first, and the list reads oldest-first from top to bottom.
  const pastJobs = useMemo(
    () => (pastWanted ? pastPages.flatMap(page => page.jobs).reverse() : EMPTY_JOBS),
    [pastWanted, pastPages]
  )
  const jobs = useMemo(() => [...pastJobs, ...futureJobs], [pastJobs, futureJobs])

  const timeZones = useMemo(() => {
    const map = new Map<string, string>()
    for (const property of propertiesQuery.data?.properties ?? []) {
      const record = property as { id?: unknown; timezone?: string }
      if (typeof record.id === 'string' && record.timezone) map.set(record.id, record.timezone)
    }
    return map
  }, [propertiesQuery.data])

  const timeZoneFor = useCallback(
    (propertyId: string) => timeZones.get(propertyId) ?? DEFAULT_PROPERTY_TIME_ZONE,
    [timeZones]
  )

  const propertyList = useMemo(() => {
    const out: { id: string; name: string }[] = []
    // No cleanings for a property still onboarding (CAP-1467).
    for (const property of pickableProperties(propertiesQuery.data?.properties ?? [])) {
      const record = property as { id?: unknown; display_name?: string; name?: string }
      if (typeof record.id !== 'string') continue
      out.push({ id: record.id, name: record.display_name || record.name || record.id })
    }
    return out
  }, [propertiesQuery.data])

  const propertyNames = useMemo(
    () => new Map(propertyList.map(property => [property.id, property.name])),
    [propertyList]
  )

  /**
   * Take the reason an action resolved off its job, at once, in the loaded pages.
   *
   * One job is one item now (CAP-531), so an action resolves one of its
   * REASONS: "Told them" on a job that is also waiting on an ack leaves the job
   * on the board with one reason fewer. Only when its last reason goes does the
   * item leave, and only then do the totals drop - by one job.
   *
   * This is for the instant only. An action can move more than the reason that
   * offered it - recording an ack also closes an open heads-up - and the client
   * does not re-derive admin-api's rules to guess which, so the action's
   * success also refetches the list and admin-api's answer replaces this one.
   * The facet counts are left alone here for the same reason.
   */
  const retireReason = useCallback(
    (propertyId: string, jobId: string, retired: readonly CleaningActionKind[]) => {
      queryClient.setQueryData<InfiniteData<ActionsPage, number>>(
        actionsQueryKey(filters),
        current => {
          if (!current) return current
          let dropped = 0
          let touched = false
          const pages = current.pages.map(page => ({
            ...page,
            items: page.items.flatMap(item => {
              if (item.property_id !== propertyId || item.job_id !== jobId) return [item]
              touched = true
              const reasons = item.reasons.filter(r => !retired.includes(r.kind))
              if (reasons.length === 0) {
                dropped += 1
                return []
              }
              return [{ ...item, reasons }]
            }),
          }))
          if (!touched) return current
          return {
            ...current,
            pages: pages.map(page => ({
              ...page,
              total: Math.max(0, page.total - dropped),
              count: Math.max(0, page.count - dropped),
              total_unfiltered: Math.max(0, page.total_unfiltered - dropped),
            })),
          }
        }
      )
    },
    [queryClient, filters]
  )

  /**
   * Keep the row an action just settled on screen, where it is now (CAP-785).
   *
   * Recorded for every action, not only the ones that empty the row: whether
   * the row goes is admin-api's call (an ack also closes a heads-up), and a
   * row it still returns is shown live over this one anyway.
   */
  const keepSettled = (variables: ActionCallArgs) => {
    const key = jobKey(variables.propertyId, variables.jobId)
    const at = rows.findIndex(row => jobKey(row.item.property_id, row.item.job_id) === key)
    if (at < 0) return
    const above = rows[at - 1]?.item
    const row: SettledRow<CleaningActionItem> = {
      key,
      item: rows[at].item,
      describe: variables.describe,
      context: variables.context,
      view: actionsView,
      after: above ? jobKey(above.property_id, above.job_id) : null,
      index: at,
    }
    setSettled(previous => [
      ...previous.filter(other => !(other.key === key && other.view === actionsView)),
      row,
    ])
  }

  /**
   * Forget a settled row once admin-api's settled answer has its job live
   * again. Kept, it would come back if the job later left the list for some
   * other reason, still offering to undo this page's earlier action. Not while
   * a refetch is out: the list then may still be the pre-action one.
   */
  const [prunedAgainst, setPrunedAgainst] = useState(items)
  if (!actionsQuery.isFetching && prunedAgainst !== items) {
    // Adjusted during render, React's pattern for state derived from a change.
    setPrunedAgainst(items)
    const live = new Set(items.map(item => jobKey(item.property_id, item.job_id)))
    if (settled.some(row => live.has(row.key))) {
      setSettled(settled.filter(row => !live.has(row.key)))
    }
  }

  /**
   * An action was taken back, or can no longer be: either way the settled row
   * no longer stands for anything, and the list is refetched for the truth.
   */
  const undone = useCallback(
    (propertyId: string, jobId: string) => {
      const key = jobKey(propertyId, jobId)
      setSettled(previous => previous.filter(row => row.key !== key))
      // The row was retired locally rather than refetched, so putting it
      // back means asking the server again - the offsets this client has
      // already consumed shifted underneath it either way.
      void queryClient.invalidateQueries({ queryKey: ['cleaning', 'actions-needed'] })
      void queryClient.invalidateQueries({ queryKey: ['cleaning', 'jobs'] })
    },
    [queryClient]
  )

  const actionMutation = useMutation({
    mutationFn: ({ propertyId, jobId, action, body }: ActionCallArgs) =>
      api.runCleaningJobAction(propertyId, jobId, action, body ?? {}),
    onMutate: ({ propertyId, jobId }: ActionCallArgs) =>
      setPendingJobs(previous => new Set(previous).add(jobKey(propertyId, jobId))),
    onSuccess: (data, variables) => {
      toast({
        title: variables.describe,
        description: variables.context,
        // Longer than the default five seconds, because the toast now carries
        // something to act on: noticing a misclick, reading which row it
        // landed on and reaching for Undo does not fit in the time it takes to
        // read a confirmation you were never going to act on.
        duration: 12000,
        action: (
          <UndoAction
            propertyId={variables.propertyId}
            jobId={variables.jobId}
            describe={variables.describe}
            context={variables.context}
            onUndone={() => undone(variables.propertyId, variables.jobId)}
            // The row's own Undo is gone for good too: forget it.
            onRefused={() => undone(variables.propertyId, variables.jobId)}
          />
        ),
      })
      keepSettled(variables)
      retireReason(variables.propertyId, variables.jobId, variables.reasons)
      // admin-api's answer replaces the instant one: an action can close more
      // than the reason that offered it (an ack also closes a heads-up), and
      // the admin-ui rule is to refresh backend state after every mutation.
      void queryClient.invalidateQueries({ queryKey: ['cleaning', 'actions-needed'] })
      // The upcoming list is derived from the same jobs and is not offset-
      // sensitive in the same way (nothing there is retired by an action), so
      // the job admin-api just returned is written straight into it and the
      // refetch that follows only has to correct ordering and the counts.
      applyCleaningJob(queryClient, data.job)
      void queryClient.invalidateQueries({ queryKey: ['cleaning', 'jobs'] })
    },
    onError: (error: Error) => {
      toast({
        title: 'That action did not go through',
        description: cleaningActionErrorMessage(error),
        variant: 'destructive',
      })
    },
    onSettled: (_data, _error, variables) =>
      setPendingJobs(previous => {
        const next = new Set(previous)
        next.delete(jobKey(variables.propertyId, variables.jobId))
        return next
      }),
  })

  const linkedRowLoaded = Boolean(
    linkedJob &&
    items.some(item => item.property_id === linkedJob.propertyId && item.job_id === linkedJob.jobId)
  )

  /**
   * Scroll to the job a heads-up email linked to, once its row is on the page.
   *
   * Depends on the rows arriving, not just on the id: the list loads
   * asynchronously, so an effect keyed on the id alone runs against an empty
   * page and silently does nothing. Does not clear the param - re-opening the
   * same link should behave the same way twice, and the ring is cheap.
   */
  useEffect(() => {
    if (!linkedJobKey || !linkedRowLoaded) return
    const separator = linkedJobKey.indexOf(':')
    const element = document.getElementById(
      actionCardDomId(linkedJobKey.slice(0, separator), linkedJobKey.slice(separator + 1))
    )
    // Optional-called, not just optional-chained on the element. `scrollIntoView`
    // is absent in jsdom and in some embedded webviews, and an effect that
    // throws takes the whole page down with it - losing the list to fail at
    // scrolling to a row that is already rendered and ringed.
    element?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
  }, [linkedJobKey, linkedRowLoaded])

  /**
   * Keep paging until the linked row is loaded.
   *
   * Infinite scroll and an email deep link pull against each other: the link
   * names one job, and that job may sit on page four. Without this the reader
   * follows a link from their inbox, lands on the first 50 rows, and nothing
   * happens - the row it promised is real but unfetched. Bounded by
   * `hasNextPage`, so a job that has since been acked (and is no longer on this
   * list at all) walks the pages once and then stops, which is the ordinary
   * case rather than an error.
   */
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = actionsQuery
  useEffect(() => {
    if (!linkedJob || linkedRowLoaded) return
    if (!hasNextPage || isFetchingNextPage) return
    void fetchNextPage()
  }, [linkedJob, linkedRowLoaded, hasNextPage, isFetchingNextPage, fetchNextPage])

  const trackerOff = settingsQuery.data?.switches.tracker_enabled === false
  const today = first?.today ?? firstJobs?.today ?? null

  // Both directions, because the list is both directions. Each `total` is its
  // own half after the filters; `total_unfiltered` is already portfolio-wide
  // and unbounded by date, so it needs no adding up.
  const totalBothWays = (firstJobs?.total ?? 0) + (pastCount?.total ?? 0)

  // What is still behind the button. Read off the last week loaded once there
  // is one, since it was counted at the same moment that week's rows were,
  // rather than subtracting rows from a total fetched earlier.
  const lastPast = pastPages[pastPages.length - 1]
  const earlierCount = pastWanted && lastPast ? lastPast.earlier : (pastCount?.total ?? 0)

  const loadEarlier = useCallback(() => {
    // The first press switches the query on; every later one asks for the week
    // before the last one loaded.
    if (!pastWanted) {
      revealPast()
      return
    }
    if (!pastQuery.hasNextPage || pastQuery.isFetchingNextPage) return
    void pastQuery.fetchNextPage()
  }, [pastWanted, revealPast, pastQuery])

  const facets: CleaningFacets | undefined =
    filters.tab === 'actions' ? first?.facets : firstJobs?.facets

  // A supporting request that failed is NOT the same as one that came back
  // empty, and defaulting one to the other is how this screen would quietly
  // lie: no crews in the picker looks like no crews exist, and a missing
  // settings read looks like the tracker is on.
  const dependencyFailures: { key: string; message: string; retry: () => void }[] = []
  if (teamsQuery.isError) {
    dependencyFailures.push({
      key: 'teams',
      message:
        'The cleaning crews could not be loaded, so nobody can be assigned from this screen and crew names show as ids.',
      retry: () => void teamsQuery.refetch(),
    })
  }
  if (settingsQuery.isError) {
    dependencyFailures.push({
      key: 'settings',
      message:
        'Whether the tracker is switched on could not be read, so this screen cannot tell you whether what is below is being kept up to date.',
      retry: () => void settingsQuery.refetch(),
    })
  }
  if (propertiesQuery.isError) {
    dependencyFailures.push({
      key: 'properties',
      message:
        'Property names and timezones could not be loaded, so properties show as ids and times fall back to Eastern.',
      retry: () => void propertiesQuery.refetch(),
    })
  }

  const refreshing =
    actionsQuery.isFetching ||
    futureQuery.isFetching ||
    pastQuery.isFetching ||
    teamsQuery.isFetching

  const runAction = (args: ActionCallArgs) => actionMutation.mutate(args)

  return (
    <div className="space-y-6">
      {/* What is on screen was computed when the request went out, not when the
          operator is looking at it, and this page gets left open. "Nothing
          needs doing" from a minute ago and from this morning are different
          claims, and only one is worth acting on. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-textFaint">
          {first?.generated_at
            ? `Computed ${formatDistanceToNow(new Date(first.generated_at), { addSuffix: true })}`
            : 'Not computed yet'}
        </p>
        <div className="flex items-center gap-3">
          <span
            className="text-xs text-textFaint"
            title="No property carries a timezone of its own, so every one resolves to Cape Cod."
          >
            Times and dates in property local time (ET)
          </span>
          <Button size="sm" variant="outline" onClick={() => setCrewMessageOpen(true)}>
            <MessageSquareText className="mr-2 h-3.5 w-3.5" />
            Message a crew
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={refreshing}
            onClick={() => void queryClient.invalidateQueries({ queryKey: ['cleaning'] })}
          >
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing' : 'Refresh'}
          </Button>
        </div>
      </div>

      {dependencyFailures.map(failure => (
        <Notice key={failure.key}>
          <span className="flex flex-wrap items-center gap-2">
            {failure.message}
            <Button size="sm" variant="outline" onClick={failure.retry}>
              Retry
            </Button>
          </span>
        </Notice>
      ))}

      {/* The switch is a wrangler var, so this reports it and never offers a
          toggle. Saying it out loud matters: "nothing needs doing" and "nothing
          is running" look identical on an empty screen. */}
      {trackerOff && (
        <Notice>
          The cleaning tracker is switched off, so no jobs are being created or updated. What is
          below is whatever was last stored, not today&rsquo;s work.
        </Notice>
      )}

      {/* Where crew mail is actually going. Configured in KV rather than by a
          switch, so without this the board gives no sign that the crews are not
          being emailed (CAP-524). */}
      <MutedCrewsNotice mutedCrews={settingsQuery.data?.muted_crews} />

      <div>
        {/* One row, pinned with the page header: what to show, and the filters
            that narrow it, stay in reach while scrolling the days. */}
        <div className="sticky top-0 z-20 border-b border-border bg-bg pb-2 pt-2">
          <CleaningFilterBar
            filters={filters}
            onChange={setFilters}
            teams={teams}
            properties={propertyList}
            facets={facets}
            // The UNFILTERED total, always: narrowing the view must never make
            // the amount of outstanding work look smaller than it is.
            needsActionCount={first?.total_unfiltered}
            {...(filters.tab === 'actions'
              ? {
                  total: first?.total,
                  totalUnfiltered: first?.total_unfiltered,
                  shown: items.length,
                }
              : {
                  total: totalBothWays,
                  totalUnfiltered: firstJobs?.total_unfiltered,
                  shown: jobs.length,
                })}
          />
        </div>

        {filters.tab === 'actions' && (
          <div className="pt-2">
            {actionsQuery.isLoading ? (
              <div className="space-y-3 pt-4">
                <LoadingSkeleton className="h-10 w-full" />
                <LoadingSkeleton className="h-10 w-full" />
              </div>
            ) : actionsQuery.isError ? (
              <div className="pt-4">
                <ErrorState
                  error={actionsQuery.error as Error}
                  onRetry={() => actionsQuery.refetch()}
                />
              </div>
            ) : (
              <div className="space-y-3 pt-4">
                {first?.truncated && (
                  <Notice>
                    Some properties could not be read in full, so this list is incomplete. Treat
                    what is missing as unknown rather than clear.
                  </Notice>
                )}
                <UnconfiguredPropertiesNotice properties={first?.unconfigured_properties ?? []} />
                <WaitingAsks today={today} />
                {rows.length > 0 ? (
                  <ActionsTable
                    rows={rows}
                    teams={teams}
                    today={today}
                    timeZoneFor={timeZoneFor}
                    pendingJobs={pendingJobs}
                    onAction={runAction}
                    onUndone={undone}
                    onOpen={setOpenJob}
                    onOpenCalendar={setCalendarJob}
                    linkedJobKey={linkedJobKey}
                  />
                ) : cleanSlateWithheld ? (
                  waitingAsks.isLoading ? (
                    <LoadingSkeleton className="h-10 w-full" />
                  ) : null
                ) : (
                  <EmptyState
                    message={
                      first?.truncated
                        ? 'Nothing to act on in the properties that could be read.'
                        : first && first.total_unfiltered > 0
                          ? 'Nothing matches these filters.'
                          : 'Nothing needs doing right now.'
                    }
                  />
                )}
                <InfiniteListFooter
                  hasMore={Boolean(actionsQuery.hasNextPage)}
                  loading={actionsQuery.isFetchingNextPage}
                  onLoadMore={() => void actionsQuery.fetchNextPage()}
                  shown={items.length}
                  total={first?.total ?? 0}
                  noun="jobs"
                />
              </div>
            )}
          </div>
        )}

        {filters.tab === 'all' && (
          <div className="pt-2">
            {futureQuery.isLoading ? (
              <div className="space-y-3 pt-4">
                <LoadingSkeleton className="h-10 w-full" />
                <LoadingSkeleton className="h-10 w-full" />
              </div>
            ) : futureQuery.isError ? (
              <div className="pt-4">
                <ErrorState
                  error={futureQuery.error as Error}
                  onRetry={() => void futureQuery.refetch()}
                />
              </div>
            ) : (
              <div className="space-y-3 pt-4">
                {(firstJobs?.truncated || pastTruncated) && (
                  <Notice>
                    Some properties could not be read in full, so days below may be missing
                    cleanings.
                  </Notice>
                )}

                {/* Above the rows, because that is the direction it loads in.
                  A "load earlier" control below the list would be pointing the
                  wrong way. */}
                {pastQuery.isError || pastCountQuery.isError ? (
                  <ErrorState
                    error={(pastQuery.error ?? pastCountQuery.error) as Error}
                    onRetry={() =>
                      void (pastQuery.isError ? pastQuery.refetch() : pastCountQuery.refetch())
                    }
                  />
                ) : (
                  <LoadEarlierButton
                    hasMore={pastWanted ? Boolean(pastQuery.hasNextPage) : earlierCount > 0}
                    loading={pastQuery.isFetching}
                    onLoadEarlier={loadEarlier}
                    days={PAST_WEEK_DAYS}
                    earlierCount={earlierCount}
                  />
                )}

                {jobs.length === 0 ? (
                  <EmptyState
                    message={
                      firstJobs?.truncated
                        ? 'No cleanings in the properties that could be read.'
                        : firstJobs && firstJobs.total_unfiltered > 0
                          ? 'No cleanings match these filters.'
                          : 'No cleanings scheduled.'
                    }
                  />
                ) : (
                  <UpcomingTable
                    jobs={jobs}
                    teams={teams}
                    today={today}
                    timeZoneFor={timeZoneFor}
                    propertyNames={propertyNames}
                    onOpen={setOpenJob}
                    onOpenCalendar={setCalendarJob}
                    onOpenStay={setOpenStay}
                    pendingJobs={pendingJobs}
                    onMove={runAction}
                  />
                )}
                <InfiniteListFooter
                  hasMore={Boolean(futureQuery.hasNextPage)}
                  loading={futureQuery.isFetchingNextPage}
                  onLoadMore={() => void futureQuery.fetchNextPage()}
                  shown={jobs.length}
                  total={totalBothWays}
                  noun="cleanings"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Read-only, so it is rendered alongside the drawer rather than inside
          it: the same calendar is wanted from a row and from an open job, and
          one copy at page level is what lets both reach it. */}
      {calendarJob && (
        <JobCalendarDialog
          // Prefixed: the drawer is a sibling keyed on the same job pair, and
          // two siblings sharing a key is a React error that shows up as one
          // of them quietly not re-mounting.
          key={`calendar:${calendarJob.propertyId}:${calendarJob.jobId}`}
          propertyId={calendarJob.propertyId}
          propertyName={calendarJob.propertyName}
          jobId={calendarJob.jobId}
          jobDay={calendarJob.day}
          teams={teams}
          timeZone={timeZoneFor(calendarJob.propertyId)}
          today={today}
          onClose={() => setCalendarJob(null)}
        />
      )}

      {crewMessageOpen && (
        <CrewMessageDialog
          teams={teams}
          // The board filter is a single crew id, 'none' (unassigned), or '' -
          // only a real crew id matches one of `teams`, so the dialog's own
          // validation against the active list already ignores the other two.
          initialTeamId={filters.crew || undefined}
          teamsLoading={teamsQuery.isLoading}
          teamsError={teamsQuery.error}
          onRetryTeams={() => void teamsQuery.refetch()}
          onClose={() => setCrewMessageOpen(false)}
        />
      )}

      {openStay && (
        <CleaningReservationDialog
          key={`stay:${openStay.reservationId}`}
          reservationId={openStay.reservationId}
          propertyId={openStay.propertyId}
          propertyName={openStay.propertyName}
          stayDay={openStay.stayDay}
          onClose={() => setOpenStay(null)}
        />
      )}

      {/* One drawer for the page: only ever one job is open, and rendering it
          per table would give the two tabs separate copies of the same state. */}
      {openJob && (
        <CleaningJobDrawer
          key={`drawer:${openJob.propertyId}:${openJob.jobId}`}
          propertyId={openJob.propertyId}
          jobId={openJob.jobId}
          propertyName={openJob.propertyName}
          teams={teams}
          timeZone={timeZoneFor(openJob.propertyId)}
          today={today}
          onClose={() => setOpenJob(null)}
          onChanged={() => {
            // Both lists are derived from the job this drawer just changed, so
            // both are stale. Refetched rather than surgically patched: the
            // drawer can change a job in ways that move it between kinds, and
            // a row edited in place would keep its old group.
            void queryClient.invalidateQueries({ queryKey: ['cleaning', 'actions-needed'] })
            void queryClient.invalidateQueries({ queryKey: ['cleaning', 'jobs'] })
          }}
          onOpenCalendar={day => setCalendarJob({ ...openJob, day })}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------------ */

/**
 * Scrolls sideways whenever its table is wider than it, at every width. It used
 * to switch to `overflow-x: visible` from the lg breakpoint, which clipped the
 * right-hand columns - the actions - instead of scrolling them into reach
 * whenever the sidebar left less room than the table's minimum width.
 */
const TABLE_WRAP = 'overflow-x-auto rounded-card border border-border bg-surface'
const TH =
  'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-textFaint whitespace-nowrap'
const TD = 'px-3 py-2 align-middle wrap-anywhere'

interface ActionsTableProps {
  /** The live rows, with the ones settled from this page kept in place (CAP-785). */
  rows: DisplayRow<CleaningActionItem>[]
  teams: CleaningTeam[]
  today: string | null
  timeZoneFor: (propertyId: string) => string
  /**
   * Every job with an action in flight, as `jobKey` pairs - a SET, not one id.
   * Each action is a whole-record read-modify-write on the backend, so two
   * overlapping actions on the same job silently overwrite each other.
   */
  pendingJobs: ReadonlySet<string>
  onAction: (args: ActionCallArgs) => void
  /** A settled row's action was taken back from the row itself. */
  onUndone: (propertyId: string, jobId: string) => void
  /** Open one job. The row is the handle; the drawer is where the work happens. */
  onOpen: (job: OpenJob) => void
  /** Open the property's month around one job, to see what surrounds it. */
  onOpenCalendar: (job: CalendarJob) => void
  /** `propertyId:jobId` a heads-up email linked to, so its row is called out. */
  linkedJobKey: string | null
}

/**
 * What to say about a property's gap.
 *
 * Branching on `missing[0]` alone labelled an EMPTY array as a rate problem,
 * which would name a perfectly configured property and send someone to fix a
 * rate that was never missing. An unrecognised gap says only that something
 * is unset rather than inventing which.
 */
function gapLabel(missing: Array<'crew' | 'rate'>): string {
  if (missing[0] === 'crew') {
    return ' - no crew, so its cleanings will be created with nobody assigned'
  }
  if (missing[0] === 'rate') {
    return ' - no agreed rate for its crew, so its cleanings will have nothing to pay'
  }
  return ' - not fully set up'
}

/**
 * Properties that are on for cleaning but cannot run on their own (CAP-381).
 *
 * Sits above the actions list rather than in it, because it is a different
 * kind of thing: every row below is one job to deal with, and this is a house
 * that will keep producing broken jobs until someone configures it once.
 *
 * It appears BEFORE the first booking at a new property, which is the whole
 * reason it exists - waiting for a job means finding out on the day a crew
 * was supposed to turn up.
 */
function UnconfiguredPropertiesNotice({
  properties,
}: {
  properties: Array<{
    property_id: string
    property_name: string | null
    missing: Array<'crew' | 'rate'>
  }>
}) {
  if (properties.length === 0) return null

  return (
    <Notice>
      <p>
        {properties.length === 1
          ? '1 property is on for cleaning but not set up:'
          : `${properties.length} properties are on for cleaning but not set up:`}
      </p>
      <ul className="mt-1 space-y-0.5">
        {properties.map(property => (
          <li key={property.property_id}>
            <Link
              to={`/properties/${property.property_id}`}
              className="underline underline-offset-2"
            >
              {property.property_name ?? property.property_id}
            </Link>
            {gapLabel(property.missing)}
          </li>
        ))}
      </ul>
    </Notice>
  )
}

/** One control a row can show. */
type ControlKey = 'assign' | 'decide' | 'ack' | 'not-required' | 'fix-rate' | 'unpin' | 'told-them'

/** A job that is no longer happening: nothing can be acked, only told. */
const JOB_IS_OFF: ReadonlySet<CleaningItemStatus> = new Set([
  'cancelled',
  'not_required',
  'completed',
])

/**
 * What one reason offers, given where its job is.
 *
 * A change ("Changed") is settled differently by state (CAP-530): for a job
 * waiting on its ack, by the ack - telling is not agreeing; for a job that is
 * off, by telling the crew not to come, since nothing there can be acked; and
 * otherwise by nothing on its own - a job with no crew yet needs one first,
 * which its other reason already offers.
 */
function controlsOffered(kind: CleaningActionKind, status: CleaningItemStatus): ControlKey[] {
  switch (kind) {
    case 'assign_team':
      return ['assign']
    case 'block_needs_decision':
      return ['decide']
    case 'paying_today_never_acked':
      return ['ack', 'not-required']
    case 'payable_problem':
      return ['fix-rate']
    case 'pinned':
      return ['unpin']
    case 'crew_heads_up':
      if (status === 'needs_ack') return ['ack']
      return JOB_IS_OFF.has(status) ? ['told-them'] : []
    case 'awaiting_ack':
      return ['ack']
    case 'completed_but_cancelled':
      return []
  }
}

/**
 * The actions table: one row per JOB, in admin-api's order (CAP-531).
 *
 * It used to be one row per reason, grouped by kind, which put a job with two
 * reasons on screen twice - under "Tell the crew" and again under "Waiting on
 * an ack", correct both times and never naming the other. A row is the job
 * now: one status, and a tag for each reason the status does not already say.
 *
 * The order is still admin-api's, untouched: each job arrives where its most
 * urgent reason ranks it, so reading top to bottom is still "what do I do
 * next". The stripe down the left edge is that most urgent reason's severity.
 */
function ActionsTable({
  rows,
  teams,
  today,
  timeZoneFor,
  pendingJobs,
  onAction,
  onUndone,
  onOpen,
  onOpenCalendar,
  linkedJobKey,
}: ActionsTableProps) {
  return (
    <div className={TABLE_WRAP}>
      {/* 980 at the least: the fixed columns take 740, and 240 is what the
          widest pair of actions a job carries needs to stay on one line. It
          still fits beside the sidebar on a 1280 screen; below that the box
          scrolls. */}
      <table className="w-full min-w-[980px] table-fixed border-separate border-spacing-0 text-sm">
        {/* Everything is sized to what it holds and the leftover lands on
            Action, which is right-aligned - so the buttons sit against the
            right edge and the slack falls where it costs nothing. */}
        <colgroup>
          <col style={{ width: 4 }} />
          <col style={{ width: 34 }} />
          <col style={{ width: 280 }} />
          <col style={{ width: 150 }} />
          <col style={{ width: 104 }} />
          {/* Room for "Asked today" and one tag side by side (CAP-710). */}
          <col style={{ width: 200 }} />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th className={TH} aria-label="Severity" />
            <th className={TH} aria-label="Calendar" />
            <th className={TH} scope="col">
              Property
            </th>
            <th className={TH} scope="col">
              When
            </th>
            <th className={TH} scope="col">
              Crew
            </th>
            <th className={TH} scope="col">
              Status
            </th>
            <th className={`${TH} text-right`} scope="col">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ item, settled }) => {
            const key = jobKey(item.property_id, item.job_id)
            const zone = timeZoneFor(item.property_id)
            return (
              <ActionItemRow
                key={key}
                item={item}
                settled={settled}
                onUndone={onUndone}
                day={item.local_date ?? localDate(item.scheduled_at, zone)}
                time={shortTime(item.scheduled_at, zone)}
                timeZone={zone}
                teams={teams}
                today={today}
                busy={pendingJobs.has(key)}
                onAction={onAction}
                onOpen={onOpen}
                onOpenCalendar={onOpenCalendar}
                highlighted={!settled && linkedJobKey === key}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

interface ActionItemRowProps {
  item: CleaningActionItem
  day: string | null
  /** The scheduled time in the property's own zone, "10am" - omitted when it can't be told. */
  time: string | null
  /** The property's own zone, for the Tentative badge's confirmation time. */
  timeZone: string
  teams: CleaningTeam[]
  today: string | null
  busy: boolean
  onAction: (args: ActionCallArgs) => void
  onOpen: (job: OpenJob) => void
  onOpenCalendar: (job: CalendarJob) => void
  /** True when a `?job=` link named this job, so it is scrolled to and ringed. */
  highlighted?: boolean
  /**
   * Set when the row was settled from this page and is only kept for the
   * record (CAP-785): greyed, saying what was done, with nothing left to do
   * but take it back.
   */
  settled?: SettledRow<CleaningActionItem>
  onUndone: (propertyId: string, jobId: string) => void
}

function ActionItemRow({
  item,
  day,
  time,
  timeZone,
  teams,
  today,
  busy,
  onAction,
  onOpen,
  onOpenCalendar,
  highlighted,
  settled,
  onUndone,
}: ActionItemRowProps) {
  const [teamChoice, setTeamChoice] = useState('')
  const [deciding, setDeciding] = useState(false)
  // The stripe is the MOST urgent reason's severity - the reason that put the
  // job where it sits in the list.
  const stripe = settled
    ? 'var(--color-border)'
    : TONE_STRIPE[CLEANING_KIND_META[item.reasons[0].kind].tone]
  // Everything but the action cell fades, so what was done stays readable.
  const faded = settled ? 'opacity-45' : ''
  const status = CLEANING_ITEM_STATUS_META[item.status]
  const askedAt = useAskedAt({
    propertyId: item.property_id,
    jobId: item.job_id,
    teamId: item.team_id,
    scheduledAt: item.scheduled_at,
  })
  const tags = reasonTags(item)
  const assignable = teams.filter(team => team.active)
  const open: OpenJob = {
    propertyId: item.property_id,
    jobId: item.job_id,
    propertyName: item.property_name || item.property_id,
  }

  /**
   * Property, day and crew, in the order the row itself reads.
   *
   * `crew` is passed only where the action is what decides it: an assignment's
   * toast has to name the crew being assigned, not the empty cell the row
   * still shows at the moment the button is clicked.
   */
  const context = (crew?: string) =>
    [
      item.property_name || item.property_id,
      formatCleaningDay(day, today),
      crew ?? teamLabel(item.team_id, teams),
    ].join(' · ')

  const run = (
    reasons: readonly CleaningActionKind[],
    action: ActionCallArgs['action'],
    describe: string,
    body?: Record<string, unknown>,
    crew?: string
  ) =>
    onAction({
      propertyId: item.property_id,
      jobId: item.job_id,
      action,
      body,
      describe,
      context: context(crew),
      reasons,
    })

  /**
   * The row's controls: what each of its reasons offers, each control once.
   *
   * Two reasons can offer the same control. A change on a job still waiting on
   * its ack is settled BY the ack (CAP-530), so "Changed" and "No ack" both
   * offer Record ack - and it is one button, credited to both, so pressing it
   * takes both off the row at once rather than leaving one behind until the
   * refresh lands. Ordered by first offer, which is the reasons' own order.
   */
  const offers = new Map<ControlKey, CleaningActionKind[]>()
  for (const reason of item.reasons) {
    for (const key of controlsOffered(reason.kind, item.status)) {
      offers.set(key, [...(offers.get(key) ?? []), reason.kind])
    }
  }

  const renderControl = (key: ControlKey, reasons: CleaningActionKind[]): ReactNode => {
    switch (key) {
      case 'assign':
        return (
          <span key={key} className="inline-flex items-center gap-2">
            <label className="sr-only" htmlFor={assignSelectDomId(item.property_id, item.job_id)}>
              Crew for {item.property_name || item.property_id}
            </label>
            <select
              id={assignSelectDomId(item.property_id, item.job_id)}
              value={teamChoice}
              disabled={busy}
              onChange={event => setTeamChoice(event.target.value)}
              className="h-7 rounded-input border border-border bg-surface2 px-2 text-sm text-text focus-visible:border-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint disabled:opacity-50"
            >
              <option value="">Pick a crew</option>
              {assignable.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              disabled={busy || !teamChoice}
              onClick={() =>
                run(
                  reasons,
                  'assign',
                  `Assigned ${teamLabel(teamChoice, teams)}`,
                  { team_id: teamChoice },
                  teamLabel(teamChoice, teams)
                )
              }
            >
              Assign
            </Button>
          </span>
        )
      case 'decide':
        return (
          <span key={key} className="inline-flex items-center gap-2">
            {/* Opens a dialog rather than deciding outright (CAP-419): a
                cleaning that needs doing and has nobody doing it is a problem,
                not a state worth having. "Not required" beside it stays one
                tap - nothing needs choosing to say a cleaning is not
                happening. */}
            <Button size="sm" disabled={busy} onClick={() => setDeciding(true)}>
              Needs cleaning
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                run(reasons, 'decide', 'Marked as not required', { needs_cleaning: false })
              }
            >
              Not required
            </Button>
          </span>
        )
      case 'ack':
        return (
          <Button
            key={key}
            size="sm"
            disabled={busy}
            onClick={() => run(reasons, 'ack', ACTION_DONE.ack)}
          >
            Record ack
          </Button>
        )
      case 'not-required':
        // The payables run pays without waiting for an ack, by design, so
        // marking the job not required is the only thing that stops money
        // moving for a cleaning that is not happening.
        return (
          <Button
            key={key}
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() =>
              run(reasons, 'not-required', 'Marked as not required, so no payable goes out')
            }
          >
            Not required
          </Button>
        )
      case 'fix-rate':
        return (
          <Button key={key} size="sm" variant="outline" asChild>
            <Link to={`/properties/${encodeURIComponent(item.property_id)}?tab=settings`}>
              Fix the rate
            </Link>
          </Button>
        )
      case 'unpin':
        return (
          <Button
            key={key}
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => run(reasons, 'unpin', ACTION_DONE.unpin)}
          >
            Unpin
          </Button>
        )
      case 'told-them':
        return (
          <Button
            key={key}
            size="sm"
            disabled={busy}
            onClick={() => run(reasons, 'told-them', ACTION_DONE['told-them'])}
          >
            Told them
          </Button>
        )
    }
  }

  return (
    <>
      <tr
        id={actionCardDomId(item.property_id, item.job_id)}
        // The whole row is the mouse target, which is what "click on it" means
        // in practice. The property name below is a real button, so the same
        // thing is reachable by keyboard - a click handler on a <tr> is not.
        onClick={() => onOpen(open)}
        className={`cursor-pointer ${
          highlighted ? 'bg-mintGlow ring-2 ring-mint ring-inset' : 'hover:bg-white/2.5'
        }`}
      >
        <td className="border-b border-border p-0" style={{ backgroundColor: stripe }} />
        <td
          // Narrower padding than TD: the column is 34 wide and the button 24,
          // so TD's 12px a side overflowed the cell on every row.
          className={`border-b border-border px-1 py-2 text-center align-middle ${faded}`}
          onClick={event => event.stopPropagation()}
        >
          <CalendarButton job={{ ...open, day }} onOpen={onOpenCalendar} />
        </td>
        <td className={`${TD} border-b border-border ${faded}`}>
          <button
            type="button"
            onClick={event => {
              event.stopPropagation()
              onOpen(open)
            }}
            className="text-left font-medium text-text hover:text-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint"
          >
            {item.property_name || item.property_id}
          </button>
          {highlighted && (
            <Badge variant="default" className="ml-2">
              From your email
            </Badge>
          )}
          {item.badges.map(badge =>
            badge === 'tentative' ? (
              <TentativeBadge
                key={badge}
                pending={item.confirmation_pending}
                timeZone={timeZone}
                className="ml-2"
              />
            ) : (
              <Badge key={badge} variant="warning" className="ml-2">
                Same day
              </Badge>
            )
          )}
          {tags.map(
            tag =>
              tag.detail && (
                <p key={tag.kind} className="mt-0.5 text-xs font-normal text-textMuted">
                  {tag.detail}
                </p>
              )
          )}
          {item.note && <JobNoteIcon note={item.note} />}
        </td>
        <td
          className={`${TD} border-b border-border whitespace-nowrap tabular-nums ${faded} ${
            day && today && day < today ? 'text-textMuted' : ''
          }`}
        >
          {formatCleaningDay(day, today)}
          {time && <span className="ml-1 text-textFaint">{time}</span>}
        </td>
        <td className={`${TD} border-b border-border ${faded}`}>
          <CrewCell teamId={item.team_id} teams={teams} />
        </td>
        <td className={`${TD} border-b border-border ${faded}`}>
          <span className="flex flex-wrap items-center gap-1.5">
            {askedAt && item.status === 'needs_ack' ? (
              <AskedBadge sentAt={askedAt} today={today} />
            ) : (
              <Badge variant={status.tone}>{status.label}</Badge>
            )}
            {tags.map(tag => (
              <Badge
                key={tag.kind}
                variant="secondary"
                title={tag.detail ?? tag.why}
                className="gap-1.5 font-normal"
              >
                <span
                  aria-hidden="true"
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: TONE_STRIPE[tag.tone] }}
                />
                {tag.label}
              </Badge>
            ))}
          </span>
        </td>
        <td
          className={`${TD} border-b border-border`}
          // Acting on a row is not opening it. Without this every one-tap
          // action would also throw the drawer over the top of its own toast.
          onClick={event => event.stopPropagation()}
        >
          {/* Wraps rather than overflowing: the widest pair a job can carry,
              plus "Saving" while one is in flight, is the one case that can
              outgrow the column on a narrow screen. */}
          <span className="flex flex-wrap items-center justify-end gap-2 whitespace-nowrap">
            {/* Which row is waiting, in the row itself. A disabled button dims
                by 50% whether it is in flight or simply not ready, so the
                dimming alone cannot say a request is out. */}
            {settled ? (
              <SettledAction settled={settled} item={item} onUndone={onUndone} />
            ) : (
              <>
                {busy && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-textMuted">
                    <Spinner />
                    Saving
                  </span>
                )}
                {[...offers].map(([key, reasons]) => renderControl(key, reasons))}
              </>
            )}
          </span>
        </td>
      </tr>

      {deciding && (
        <CleaningDecideDialog
          propertyId={item.property_id}
          propertyName={item.property_name || item.property_id}
          day={formatCleaningDay(day, today)}
          teams={teams}
          busy={busy}
          onCancel={() => setDeciding(false)}
          onConfirm={teamId => {
            setDeciding(false)
            run(
              ['block_needs_decision'],
              'decide',
              teamId
                ? `Cleaning confirmed for ${teamLabel(teamId, teams)}`
                : 'Marked as needing a cleaning',
              { needs_cleaning: true, ...(teamId ? { team_id: teamId } : {}) },
              teamId ? teamLabel(teamId, teams) : undefined
            )
          }}
        />
      )}
    </>
  )
}

/**
 * What a settled row says in place of its buttons (CAP-785): what was done to
 * it, and the same Undo the toast offered, for as long as the row is on
 * screen rather than for as long as the toast is.
 */
function SettledAction({
  settled,
  item,
  onUndone,
}: {
  settled: SettledRow<CleaningActionItem>
  item: CleaningActionItem
  onUndone: (propertyId: string, jobId: string) => void
}) {
  const { undo, busy } = useUndoCleaningAction({
    propertyId: item.property_id,
    jobId: item.job_id,
    describe: settled.describe,
    context: settled.context,
    onUndone: () => onUndone(item.property_id, item.job_id),
    // Refused means something else wrote the job since: an Undo that can never
    // work again should not stay on screen until reload.
    onRefused: () => onUndone(item.property_id, item.job_id),
  })
  return (
    <>
      <span className="inline-flex items-center gap-1.5 text-textMuted">
        <Check className="h-4 w-4 text-mint" aria-hidden="true" />
        {settled.describe}
      </span>
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        aria-label={`Undo: ${settled.describe}`}
        onClick={undo}
      >
        {busy ? 'Undoing' : 'Undo'}
      </Button>
    </>
  )
}

/**
 * The Stay cell for anything that is not a guest booking: an owner's own stay
 * or an Uplisting block says so before its dates, because an unlabelled span
 * reads as a guest's (CAP-795). Plain text - there is no reservation to open.
 */
function BlockStay({ label, stay }: { label: string | null; stay: string | null }) {
  if (!label) return <>{stay ?? '—'}</>
  return (
    <>
      <span className="font-medium text-text">{label}</span>
      {stay && <> · {stay}</>}
    </>
  )
}

/**
 * A job's note, as an icon beside the property name that shows the note on
 * hover or focus. Both tables use it, so a note looks the same wherever the job
 * is listed. A button rather than a bare icon so the note is reachable from the
 * keyboard; it stops the click so reading the note does not open the drawer.
 */
function JobNoteIcon({ note }: { note: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={event => event.stopPropagation()}
          className="ml-1.5 inline-flex align-middle text-textMuted hover:text-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint"
          aria-label={`Note: ${note}`}
        >
          <StickyNote className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="whitespace-pre-wrap">{note}</p>
      </TooltipContent>
    </Tooltip>
  )
}

interface UpcomingTableProps {
  jobs: CleaningJob[]
  teams: CleaningTeam[]
  today: string | null
  timeZoneFor: (propertyId: string) => string
  propertyNames: Map<string, string>
  onOpen: (job: OpenJob) => void
  onOpenCalendar: (job: CalendarJob) => void
  onOpenStay: (stay: OpenStay) => void
  /** Jobs with an action in flight, which cannot be picked up meanwhile. */
  pendingJobs: ReadonlySet<string>
  /** A drop on another day: the reschedule to run, described for its toast. */
  onMove: (args: ActionCallArgs) => void
}

/**
 * The upcoming table, grouped by the day the crew works, with each day's crew
 * load stated on its own header rather than counted by eye.
 */
function UpcomingTable({
  jobs,
  teams,
  today,
  timeZoneFor,
  propertyNames,
  onOpen,
  onOpenCalendar,
  onOpenStay,
  pendingJobs,
  onMove,
}: UpcomingTableProps) {
  /**
   * Each job with its property-local day, whether it opens a new day, and that
   * day's crew load.
   *
   * Computed in one pass rather than per row: the load used to be a `filter`
   * over every job inside the render of each job, which is quadratic and gets
   * worse with every batch infinite scroll appends. Carrying "the previous day"
   * through a `map` callback is also a render-phase mutation, so this is a
   * plain loop.
   */
  const grouped = useMemo(() => {
    const dayOf = (job: CleaningJob) =>
      job.local_date ?? localDate(job.scheduled_at, timeZoneFor(job.property_id))

    const loadByDay = new Map<string, Map<string, number>>()
    for (const job of jobs) {
      const day = dayOf(job)
      if (!day || !job.team_id) continue
      const load = loadByDay.get(day) ?? new Map<string, number>()
      load.set(job.team_id, (load.get(job.team_id) ?? 0) + 1)
      loadByDay.set(day, load)
    }

    return groupByDay(jobs, dayOf).map(group => ({
      ...group,
      load: (group.day && loadByDay.get(group.day)) || new Map<string, number>(),
    }))
  }, [jobs, timeZoneFor])

  /** What a row shows that is worked out from the job, for the row and its preview alike. */
  const rowFacts = (job: CleaningJob, day: string | null) => {
    const zone = timeZoneFor(job.property_id)
    return {
      job,
      today,
      movedFrom: formatMovedFrom(job.scheduled_at, job.checkout_at, zone, today),
      stay: formatStay(job.stay_checkin_at, job.checkout_at, zone, today),
      stayDay: localDate(job.stay_checkin_at, zone),
      teams,
      propertyName: propertyNames.get(job.property_id) || job.property_id,
      // A guest arriving the same day the crew cleans, which is the tightest
      // thing on this list. Compared as DATES in the property's own zone, not
      // as instants: "same day" is a fact about the crew's working day, and a
      // 10am clean with a 4pm arrival is six hours apart on one date.
      sameDay: job.next_checkin_at != null && localDate(job.next_checkin_at, zone) === day,
      substate: cleaningStateBadge(job),
      day,
    }
  }

  /** The row itself, lifted, for what follows the pointer while it is dragged. */
  const renderPreview = (job: CleaningJob) => (
    <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
      <UpcomingColumns />
      <tbody>
        <UpcomingRowPreview
          {...rowFacts(
            job,
            job.local_date ?? localDate(job.scheduled_at, timeZoneFor(job.property_id))
          )}
        />
      </tbody>
    </table>
  )

  const moveJob = ({
    job,
    propertyName,
    fromDay,
    toDay,
    scheduledAt,
  }: {
    job: CleaningJob
    propertyName: string
    fromDay: string
    toDay: string
    scheduledAt: string
  }) =>
    onMove({
      propertyId: job.property_id,
      jobId: job.id,
      action: 'reschedule',
      body: { scheduled_at: scheduledAt },
      describe: 'Cleaning moved',
      // Both days, so the toast answers "did it go where I dropped it" - the
      // row itself has already left the group it was in.
      context: [
        propertyName,
        `${formatCleaningDay(fromDay, today)} \u2192 ${formatCleaningDay(toDay, today)}`,
        // The drawer says this before a move; a drop has no room to, so it is
        // said after, while Undo is still on the toast.
        job.acked_at ? 'Ack cleared' : null,
      ]
        .filter(Boolean)
        .join(' \u00b7 '),
      reasons: [],
    })

  return (
    <DayDragProvider
      today={today}
      timeZoneFor={timeZoneFor}
      onMove={moveJob}
      renderPreview={renderPreview}
    >
      <div className={TABLE_WRAP}>
        <table className="w-full min-w-[930px] table-fixed border-separate border-spacing-0 text-sm">
          <UpcomingColumns />
          <thead>
            <tr>
              <th className={TH} aria-label="Crew colour" />
              <th className={TH} aria-label="Move to another day" />
              <th className={TH} aria-label="Calendar" />
              <th className={TH} scope="col">
                Property
              </th>
              <th className={TH} scope="col">
                Crew
              </th>
              <th className={TH} scope="col">
                Status
              </th>
              <th className={TH} scope="col">
                Stay
              </th>
            </tr>
          </thead>
          {grouped.map(({ day, jobs: dayJobs, load }) => (
            <DayGroup
              key={day ?? 'unknown'}
              day={day}
              header={
                <tr>
                  <th
                    colSpan={7}
                    scope="colgroup"
                    className="border-b border-t border-border bg-surface2 px-3 py-1.5 text-left text-xs font-semibold text-text"
                  >
                    {formatCleaningDay(day, today)}
                    <span className="float-right inline-flex gap-3 font-normal text-textFaint">
                      {teams.map(team => {
                        const count = load.get(team.id) ?? 0
                        if (count === 0) return null
                        return (
                          <span key={team.id} className="inline-flex items-center gap-1.5">
                            <CrewDot color={team.color ?? null} />
                            {team.short_name || team.name} {count}
                          </span>
                        )
                      })}
                    </span>
                  </th>
                </tr>
              }
            >
              {dayJobs.map(job => (
                <UpcomingRow
                  key={`${job.property_id}:${job.id}`}
                  {...rowFacts(job, day)}
                  pending={pendingJobs.has(jobKey(job.property_id, job.id))}
                  onOpen={onOpen}
                  onOpenCalendar={onOpenCalendar}
                  onOpenStay={onOpenStay}
                />
              ))}
            </DayGroup>
          ))}
        </table>
      </div>
    </DayDragProvider>
  )
}

/** The widths both the table and the row preview use, so the preview lines up with it. */
function UpcomingColumns() {
  return (
    <colgroup>
      <col style={{ width: 4 }} />
      <col style={{ width: 30 }} />
      <col style={{ width: 34 }} />
      <col style={{ width: 280 }} />
      <col style={{ width: 110 }} />
      <col style={{ width: 196 }} />
      <col />
    </colgroup>
  )
}

/**
 * A row as it looks while it is being carried: the same cells in the same
 * columns, with a grip that is only drawn. Not a second draggable - the real
 * row is still where it was, dimmed.
 */
function UpcomingRowPreview(
  props: Omit<UpcomingRowCellsProps, 'askedAt' | 'onOpen' | 'onOpenCalendar' | 'onOpenStay'>
) {
  const { job, teams } = props
  const askedAt = useAskedAt({
    propertyId: job.property_id,
    jobId: job.id,
    teamId: job.team_id,
    scheduledAt: job.scheduled_at,
  })
  const none = () => undefined
  return (
    <tr className="bg-surface2">
      <RowColour color={teamColor(job.team_id, teams)} />
      <td className="border-b border-border px-0 py-2 text-center align-middle text-mint">
        <GripVertical className="mx-auto h-4 w-4" aria-hidden="true" />
      </td>
      <UpcomingRowCells
        {...props}
        askedAt={askedAt}
        onOpen={none}
        onOpenCalendar={none}
        onOpenStay={none}
      />
    </tr>
  )
}

interface UpcomingRowProps {
  job: CleaningJob
  /** An action on this job is in flight, so it cannot be picked up. */
  pending: boolean
  today: string | null
  /**
   * Where the cleaning came from, but ONLY when an operator has moved it.
   *
   * There is no Time column any more: it read "10am" on effectively every row,
   * because it is the global checkout time applied to every job - a constant
   * rendered in a column (CAP-420). Where a moved job SAT BEFORE is real
   * information though, and the one thing the day grouping cannot say: the row
   * is already filed under the day it happens, so naming the destination here
   * only repeats the header above it. Already spelled by `formatMovedFrom`.
   */
  movedFrom: string | null
  /** The stay this cleaning follows, or null for a block. */
  stay: string | null
  /** Its check-in day in the property's zone, for the stay dialog's month. */
  stayDay: string | null
  teams: CleaningTeam[]
  propertyName: string
  sameDay: boolean
  substate: { label: string; tone: string } | null
  /** The property's own day for this cleaning, which the calendar opens on. */
  day: string | null
  onOpen: (job: OpenJob) => void
  onOpenCalendar: (job: CalendarJob) => void
  onOpenStay: (stay: OpenStay) => void
}

/** The crew-colour edge of a row. */
function RowColour({ color }: { color: string | null }) {
  return <td className="p-0" style={{ backgroundColor: color ?? 'var(--color-border)' }} />
}

interface UpcomingRowCellsProps extends Omit<UpcomingRowProps, 'pending' | 'dayHeader' | 'load'> {
  /** When the crew was last asked about this job, if they were. */
  askedAt: ReturnType<typeof useAskedAt>
}

/**
 * Everything on a cleaning's row after its grip, as plain cells.
 *
 * Separate from `UpcomingRow` so the preview that follows the pointer while a
 * row is dragged is built from the same cells rather than a lookalike: it is
 * the row, not a picture of one, and cannot drift from it.
 */
function UpcomingRowCells({
  job,
  askedAt,
  today,
  movedFrom,
  stay,
  stayDay,
  teams,
  propertyName,
  sameDay,
  substate,
  day,
  onOpen,
  onOpenCalendar,
  onOpenStay,
}: UpcomingRowCellsProps) {
  const open: OpenJob = { propertyId: job.property_id, jobId: job.id, propertyName }

  return (
    <>
      <td
        className={`${TD} border-b border-border text-center`}
        onClick={event => event.stopPropagation()}
      >
        <CalendarButton job={{ ...open, day }} onOpen={onOpenCalendar} />
      </td>
      <td className={`${TD} border-b border-border`}>
        <button
          type="button"
          onClick={event => {
            event.stopPropagation()
            onOpen(open)
          }}
          className="text-left font-medium text-text hover:text-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint"
        >
          {propertyName}
        </button>
        {job.note && <JobNoteIcon note={job.note} />}
        {movedFrom && <span className="block text-xs tabular-nums text-amber">{movedFrom}</span>}
      </td>
      <td className={`${TD} border-b border-border`}>
        <CrewCell teamId={job.team_id} teams={teams} />
      </td>
      <td className={`${TD} border-b border-border`}>
        {askedAt && job.substate === 'needs_ack' ? (
          <AskedBadge sentAt={askedAt} today={today} />
        ) : (
          substate &&
          (job.substate === 'tentative' ? (
            <TentativeBadge
              pending={job.confirmation_pending}
              timeZone={job.time_zone ?? DEFAULT_PROPERTY_TIME_ZONE}
            />
          ) : (
            <Badge variant={substate.tone as 'warning' | 'success' | 'secondary' | 'destructive'}>
              {substate.label}
            </Badge>
          ))
        )}
        {sameDay && (
          <Badge variant="warning" className="ml-2">
            Same day
          </Badge>
        )}
        {/* Pinned sits with the other badges rather than in a column of its
              own - it is a state, and it was the only thing the Stay column
              ever rendered. */}
        {job.pinned_at && (
          <Badge variant="secondary" className="ml-2">
            Pinned
          </Badge>
        )}
      </td>
      <td className={`${TD} border-b border-border whitespace-nowrap text-textMuted`}>
        {stay && job.source.kind === 'reservation' ? (
          <button
            type="button"
            // Opening the stay is not opening the job, so the row's own
            // handler must not fire underneath it. On the button rather
            // than the cell: a block's cell holds nothing to click, and
            // stopping there made the widest column on the row a dead zone
            // where clicking used to open the drawer.
            onClick={event => {
              event.stopPropagation()
              onOpenStay({
                reservationId: job.source.id,
                propertyId: job.property_id,
                propertyName,
                stayDay,
              })
            }}
            className="text-left hover:text-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint"
            // The dates are the label a sighted operator clicks; a screen
            // reader needs to be told what clicking them does.
            aria-label={`Stay behind this cleaning: ${stay}`}
          >
            {stay}
          </button>
        ) : (
          <BlockStay label={blockLabel(job.source.kind)} stay={stay} />
        )}
      </td>
    </>
  )
}

function UpcomingRow({
  job,
  pending,
  today,
  movedFrom,
  stay,
  stayDay,
  teams,
  propertyName,
  sameDay,
  substate,
  day,
  onOpen,
  onOpenCalendar,
  onOpenStay,
}: UpcomingRowProps) {
  const color = teamColor(job.team_id, teams)
  const askedAt = useAskedAt({
    propertyId: job.property_id,
    jobId: job.id,
    teamId: job.team_id,
    scheduledAt: job.scheduled_at,
  })
  const open: OpenJob = { propertyId: job.property_id, jobId: job.id, propertyName }

  return (
    <DraggableRow
      job={job}
      propertyName={propertyName}
      day={day}
      pending={pending}
      onClick={() => onOpen(open)}
      className={`cursor-pointer hover:bg-white/2.5 ${job.confirmed_at ? '' : 'opacity-60'}`}
      leading={<RowColour color={color} />}
    >
      <UpcomingRowCells
        job={job}
        askedAt={askedAt}
        today={today}
        movedFrom={movedFrom}
        stay={stay}
        stayDay={stayDay}
        teams={teams}
        propertyName={propertyName}
        sameDay={sameDay}
        substate={substate}
        day={day}
        onOpen={onOpen}
        onOpenCalendar={onOpenCalendar}
        onOpenStay={onOpenStay}
      />
    </DraggableRow>
  )
}
